import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '~/api/queryKeys';

import { getExportTemplatesConfig } from '../services/export.service';
import type {
  ExportConnector,
  ExportFormatOption,
  ExportTemplateConnector,
  ExportTemplateDefinition,
  ExportRunOption,
  ExportTransformation,
} from '../types/export.types';

/**
 * The static export-templates config: version-stamped, tenant-independent and identical
 * for every caller — it changes only on a service deploy. Cached 10 minutes, not forever:
 * a tab left open across a deploy kept the old config (columns without `filterable`) and
 * showed filters as unavailable until a full reload.
 *
 * `enabled` gates the fetch so a closed dialog costs nothing.
 */
/** How long the templates config is reused before a refetch picks up a deploy. */
const TEMPLATES_STALE_MS = 10 * 60 * 1000;

export function useExportTemplatesConfig(enabled = true) {
  return useQuery({
    queryKey: queryKeys.exports.templates(),
    queryFn: getExportTemplatesConfig,
    staleTime: TEMPLATES_STALE_MS,
    enabled,
    refetchOnWindowFocus: false,
  });
}

/** Formats offered when the config can't be read — the set the dialog shipped with. */
const FALLBACK_FORMATS: readonly ExportFormatOption[] = [
  {
    code: 'CSV',
    label: 'CSV',
    extension: '.csv',
    delimiter: ',',
    contentType: 'text/csv',
    default: true,
    note: '',
    recommendedForFeedTemplates: false,
  },
  {
    code: 'TSV',
    label: 'TSV',
    extension: '.tsv',
    delimiter: '\t',
    contentType: 'text/tab-separated-values',
    default: false,
    note: '',
    recommendedForFeedTemplates: false,
  },
  {
    code: 'TXT',
    label: 'TXT',
    extension: '.txt',
    delimiter: ',',
    contentType: 'text/plain',
    default: false,
    note: '',
    recommendedForFeedTemplates: false,
  },
];

interface ConnectorTemplates {
  /** This connector's templates, default first. Empty while loading or on error. */
  templates: ExportTemplateDefinition[];
  /** The connector's default template code, or `null` when none is known. */
  defaultTemplateCode: string | null;
  /** File formats to offer — from the config, falling back to the built-in set. */
  formats: readonly ExportFormatOption[];
  /** Transformation code → its before/after pair, for the "what does this do" popover. */
  transformationsByCode: Record<string, ExportTransformation>;
  /** The connector's own entry, for its `specUrl` / column list. */
  connector: ExportTemplateConnector | undefined;
  /** Run-shape switches that apply to this connector (`flattenAdditionalFeatures` on META). */
  runOptions: ExportRunOption[];
  isPending: boolean;
  isError: boolean;
}

/**
 * The slice of the config that applies to one connector, matched on
 * `enrichmentConnectorCode` (`GMC` / `META`) — the value the export sites
 * already pass and the one `enrichment_job.connector_code` stores. The
 * service's own `connectorCode` (`GOOGLE_MERCHANT_CENTER`) is a display key and
 * is deliberately not used for matching.
 *
 * Degrades rather than blocks: with no config the caller gets no templates and
 * the fallback formats, which is exactly the pre-template behaviour.
 */
export function useExportConnectorTemplates(
  connector: ExportConnector,
  enabled = true,
): ConnectorTemplates {
  const { data, isPending, isError } = useExportTemplatesConfig(enabled);

  return useMemo(() => {
    const entry = data?.connectors.find((c) => c.enrichmentConnectorCode === connector);
    // Default first so the pre-selected option is also the first radio item.
    const templates = entry
      ? [...entry.templates].sort((a, b) => Number(b.default) - Number(a.default))
      : [];
    const transformationsByCode = Object.fromEntries(
      (data?.transformations ?? []).map((t) => [t.code, t]),
    );
    return {
      templates,
      defaultTemplateCode: templates.find((t) => t.default)?.code ?? null,
      formats: data?.settings.formats.length ? data.settings.formats : FALLBACK_FORMATS,
      transformationsByCode,
      connector: entry,
      // Only the switches that do something for this connector (`appliesTo` names
      // the display code, e.g. META_COMMERCE).
      runOptions: (data?.settings.options ?? []).filter(
        (option) => entry !== undefined && option.appliesTo.includes(entry.connectorCode),
      ),
      isPending: enabled && isPending,
      isError,
    };
  }, [data, connector, enabled, isPending, isError]);
}
